import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sessionsAPI } from '../services/api';
import toast from 'react-hot-toast';

const modes = [
  {
    id: 'practice',
    name: 'Practice Mode',
    icon: '📚',
    description: 'Learn at your own pace. Select specific topics and difficulty levels.',
    color: 'blue',
    isPremiumOnly: false // Free users get 1 attempt
  },
  {
    id: 'timed',
    name: 'Timed Challenge',
    icon: '⚡',
    description: '30 seconds per question. Test your speed and knowledge!',
    color: 'yellow',
    isPremiumOnly: true
  },
  {
    id: 'exam',
    name: 'Exam Simulation',
    icon: '🎯',
    description: '20 random questions. 30-minute timer. Pass requirement: 15/20 (75%). Real test conditions!',
    color: 'green',
    isPremiumOnly: true
  },
  {
    id: 'review',
    name: 'Review Mistakes',
    icon: '🔄',
    description: 'Focus on questions you got wrong. Spaced repetition learning!',
    color: 'red',
    isPremiumOnly: true
  }
];

export default function ModeSelection() {
  const navigate = useNavigate();
  const { hasPaid } = useAuth();
  const [quizAccess, setQuizAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkQuizAccess();
  }, []);

  const checkQuizAccess = async () => {
    try {
      const response = await sessionsAPI.canAccessQuiz();
      setQuizAccess(response.data);
    } catch (error) {
      console.error('Failed to check quiz access:', error);
      toast.error('Failed to check access');
    } finally {
      setLoading(false);
    }
  };

  const handleModeSelect = async (modeId, isPremiumOnly) => {
    // Check if mode is premium-only and user hasn't paid
    if (isPremiumOnly && !hasPaid) {
      toast.error('🔒 This mode is Premium only!');
      navigate('/payment');
      return;
    }

    // For practice mode, check if free user has attempts left
    if (modeId === 'practice' && !hasPaid) {
      if (!quizAccess || !quizAccess.canAccess) {
        toast.error('You\'ve used your free quiz! Upgrade to continue.');
        navigate('/upgrade-required');
        return;
      }
    }

    // Navigate to the mode
    if (modeId === 'practice') {
      navigate('/categories');
    } else if (modeId === 'exam') {
      navigate('/quiz?mode=exam');
    } else if (modeId === 'timed') {
      navigate('/categories?mode=timed');
    } else if (modeId === 'review') {
      navigate('/quiz?mode=review');
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800',
      yellow: 'from-yellow-500 to-yellow-700 hover:from-yellow-600 hover:to-yellow-800',
      green: 'from-green-500 to-green-700 hover:from-green-600 hover:to-green-800',
      red: 'from-red-500 to-red-700 hover:from-red-600 hover:to-red-800'
    };
    return colors[color];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Choose Game Mode</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
            >
              ← Back
            </button>
          </div>

          {/* Free Trial Status */}
          {!hasPaid && quizAccess && (
            <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="font-semibold text-yellow-800">
                {quizAccess.canAccess ? (
                  <>🎁 Free Trial: {quizAccess.attemptsLimit - quizAccess.attemptsUsed} quiz remaining</>
                ) : (
                  <>🔒 Free trial used. Upgrade to continue!</>
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {modes.map((mode) => {
              const isLocked = mode.isPremiumOnly && !hasPaid;

              return (
                <div
                  key={mode.id}
                  onClick={() => handleModeSelect(mode.id, mode.isPremiumOnly)}
                  className={`relative bg-gradient-to-br ${getColorClasses(mode.color)} rounded-lg p-8 cursor-pointer transition-all transform hover:scale-105 shadow-lg text-white ${
                    isLocked ? 'opacity-75' : ''
                  }`}
                >
                  {/* Premium Badge */}
                  {isLocked && (
                    <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                      <span className="mr-1">🔒</span> PREMIUM
                    </div>
                  )}

                  <div className="text-6xl mb-4 text-center">
                    {isLocked ? '🔒' : mode.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-center">{mode.name}</h3>
                  <p className="text-center text-white/90">{mode.description}</p>

                  {isLocked && (
                    <div className="mt-4 text-center">
                      <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                        Upgrade for $14.99 CAD
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
